import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  MenuItem,
  IconButton,
  Grid,
  TextField,
  CircularProgress,
  Tabs,
  Tab,
  FormControl,
  InputLabel,
  Select,
  Fab,
  Paper,
  Tooltip,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { NuvvenDivider } from "../../../ReservationManagement/Summary/Components/Divider";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import { ALLOCATION_RULE_CATEGORY, allocationRuleCategoryLabels, IAllocationCategoryRule, IAllocationRule, IRuleItem, SelectionRuleCategoryLables, updateAllocationRules, SelectionRuleCategory, RuleTrigger, Operator, FulfillmentType, PartnerSkills, ruleTypeOptions, ruleTriggerOptions, operatorOptions, fulfillmentTypeOptions, skillOptions, labels, ruleDescriptions } from "./utils";
import { ApolloError, useLazyQuery, useMutation } from "@apollo/client";
import { GET_ALLOCATION_RULES_BY_CATEGORIES, SAVE_ALLOCATION_RULES } from "../../../../../graphql/allocationRules/allocationRules";
import { useSnackBar } from "../../../../common/SnackBarContext/SnackBarContext";
import { formatGraphQLErrorMessage } from "../../../../common/utils";
import { SnackBarVariant } from "../../../../common/SnackbarWrapper/SnackbarWrapper";
import { useSelector } from "react-redux";
import { IAppState } from "../../../../../store";
import { BOOKING_CATEGORY } from "../../../../../reducers/bookings/types";
import theme from "../../../../common/Theme";
import InfoIcon from '@mui/icons-material/Info';
import { ReactComponent as RentalIcon } from "../../../../../assets/icons/rental-icon.svg"
import { ReactComponent as CourtesyCarIcon } from "../../../../../assets/icons/courtesy-car-icon.svg"
import { ReactComponent as TestDriveIcon } from "../../../../../assets/icons/test-drive-icon.svg"
import { ReactComponent as SubscriptionIcon } from "../../../../../assets/icons/subscription-icon.svg"
import { ReactComponent as ServiceIcon } from "../../../../../assets/icons/icon-service.svg"
import { ReactComponent as DeFleetIcon } from "../../../../../assets/icons/icon-de-fleet.svg"
import { ReactComponent as BranchTransferIcon } from "../../../../../assets/icons/icon-move.svg"

const renderMessage = (message: string) => (
  <Grid item xs={12}>
    <Paper elevation={3} sx={{ padding: 2, margin: "0 auto", width: "auto", maxWidth: "90%" }}>
      <Typography
        variant="h4"
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          textAlign: "center",
          wordBreak: "break-word",
          overflowWrap: "break-word",
          whiteSpace: "normal",
          maxWidth: "100%",
        }}
      >
        {message}
      </Typography>
    </Paper>
  </Grid>
);

const DriverAllocationRules: React.FC = () => {
  const snackbar = useSnackBar();
  const userState = useSelector((state: IAppState) => state.userReducer);
  const [allocationRules, setAllocationRules] = useState<IAllocationRule[]>([]);
  const isSupplierPortalEnabled = userState.currentOrganisation?.supplierPortalEnabled || false;
  const activeRules = [...userState.currentOrganisation.bookingCategories, ...(userState.currentOrganisation.internalMovementCategories || [])].filter(category => category.isActive === true && category.isDriverAllocationRuleEnabled === true)
  const activeTypesArray = activeRules.map(rule => rule.type as unknown as ALLOCATION_RULE_CATEGORY);
  const [allocationRuleTypeTabIndex, setAllocationRuleTypeTabIndex] = useState<number>(0)
  const [allocationRuleCategory, setAllocationRuleCategory] = useState<ALLOCATION_RULE_CATEGORY>();
  const [rulesByCategory, setRulesByCategory] = useState<Record<string, IAllocationCategoryRule[]>>({});
  const [selectedRuleType, setSelectedRuleType] = useState<SelectionRuleCategory | ''>('');
  const [selectedRuleTrigger, setSelectedRuleTrigger] = useState<RuleTrigger | ''>('');
  const [selectedOperator, setSelectedOperator] = useState<Operator | ''>('');
  const [distanceInKm, setDistanceInKm] = useState<number>(0);
  const [fulfillmentType, setFulfillmentType] = useState<FulfillmentType | ''>('');
  const [tier, setTier] = useState<string | ''>('');
  const [skill, setSkill] = useState<PartnerSkills | ''>('');

  const processAllocationRules = (rules: IAllocationRule[]) => {
    const newAllocationRules = updateAllocationRules(rules, activeTypesArray, isSupplierPortalEnabled);
    const initialRules: Record<string, IAllocationCategoryRule[]> = {};

    newAllocationRules.forEach(({ category, allocationRules }) => {
      initialRules[category] = allocationRules;
    });

    setRulesByCategory(initialRules);
    setAllocationRuleCategory(activeTypesArray[allocationRuleTypeTabIndex]);
  };

  const [loadAllocationRulesByCategories, { loading }] = useLazyQuery(GET_ALLOCATION_RULES_BY_CATEGORIES, {
    fetchPolicy: 'network-only',
    onCompleted: ({ allocationRulesByCategories }) => {
      setAllocationRules(allocationRulesByCategories);
      processAllocationRules(allocationRulesByCategories);
    },
    onError: (error: ApolloError) => {
      snackbar({
        message: formatGraphQLErrorMessage(error.message),
        variant: SnackBarVariant.ERROR
      });
    },
  })

  const [saveAllocationRulesMutation, { loading: saveAllocationRulesLoading }] = useMutation(SAVE_ALLOCATION_RULES, {
    onCompleted: ({ saveAllocationRules }) => {
      snackbar({
        message: 'Driver Allocation Rules Saved Successfully!',
        variant: SnackBarVariant.SUCCESS
      });
      setAllocationRules(saveAllocationRules);
      processAllocationRules(saveAllocationRules);
    },
    onError: (error: ApolloError) => {
      snackbar({
        message: formatGraphQLErrorMessage(error.message),
        variant: SnackBarVariant.ERROR
      });
    },
  })

  const getBookingTypeIcon = (type: string) => {
    switch (type) {
      case ALLOCATION_RULE_CATEGORY.RENTAL:
        return <RentalIcon width={50} height={50} fill="secondary" />

      case ALLOCATION_RULE_CATEGORY.SUBSCRIPTION:
        return <SubscriptionIcon width={50} height={50} fill="secondary" />

      case ALLOCATION_RULE_CATEGORY.TEST_DRIVE:
        return <TestDriveIcon width={50} height={50} fill="red" />

      case ALLOCATION_RULE_CATEGORY.COURTESY_CAR:
        return <CourtesyCarIcon width={50} height={50} fill="secondary" />

      case ALLOCATION_RULE_CATEGORY.SERVICE:
        return <ServiceIcon width={50} height={50} fill="secondary" />

      case ALLOCATION_RULE_CATEGORY.DEFLEET:
        return <DeFleetIcon width={50} height={50} fill="secondary" />

      case ALLOCATION_RULE_CATEGORY.BRANCH_TRANSFER:
        return <BranchTransferIcon width={50} height={50} fill="secondary" />

      default:
        return <RentalIcon width={50} height={50} fill="secondary" />
    }
  }

  const selectedCategory = activeRules[allocationRuleTypeTabIndex]?.type;
  const allocationRuleCards = rulesByCategory[selectedCategory] || [];

  const handleSave = () => {
    const hasInvalidRules = Object.entries(rulesByCategory).some(([category, updatedRules]) => {
      return updatedRules.some(rule => {
        if (!rule.rules.length) {
          snackbar({
            message: formatGraphQLErrorMessage(
              `No ${SelectionRuleCategoryLables[rule.ruleCategory]} found for ${allocationRuleCategoryLabels[category as ALLOCATION_RULE_CATEGORY]}. Please add at least one rule before proceeding.`
            ),
            variant: SnackBarVariant.ERROR
          });
          return true;
        }
        return false;
      });
    });

    if (hasInvalidRules) return;

    const updatedData = Object.entries(rulesByCategory).map(([category, updatedRules]) => {
      const existingCategoryData = allocationRules.find(item => item.category === category);
      return {
        ...existingCategoryData,
        category,
        allocationRules: updatedRules,
      };
    });

    saveAllocationRulesMutation({
      variables: {
        allocationRules: updatedData
      }
    });
  };

  useEffect(() => {
    if (activeTypesArray.length > 0) {
      loadAllocationRulesByCategories({
        variables: {
          categories: activeTypesArray,
        },
      });
    }
  }, []);


  const handleDeleteRule = (ruleCategory: string, ruleIndex: number) => {
    setRulesByCategory((prev) => ({
      ...prev,
      [selectedCategory]: prev[selectedCategory]?.map((card) =>
        card.ruleCategory === ruleCategory
          ? {
            ...card,
            rules: card.rules
              .filter((_, idx) => idx !== ruleIndex)
              .map((rule, idx) => ({ ...rule, sequence: idx + 1 })),
          }
          : card
      ) || [],
    }));
  };


  const [draggedRule, setDraggedRule] = useState<{ ruleCategory: string; index: number } | null>(null);

  const handleDragStart = (ruleCategory: string, index: number) => {
    setDraggedRule({ ruleCategory, index });
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  };


  const handleDrop = (ruleCategory: string, droppedIndex: number) => {
    if (!draggedRule || draggedRule.ruleCategory !== ruleCategory || draggedRule.index === droppedIndex) {
      setDraggedRule(null);
      return;
    }

    setRulesByCategory((prev) => {
      // Clone previous state to ensure immutability
      const updatedCategories = { ...prev };

      // Find the category to update
      const categoryData = updatedCategories[selectedCategory] || [];

      // Update the correct ruleCategory
      const updatedCards = categoryData.map((card) => {
        if (card.ruleCategory === ruleCategory) {
          const reorderedRules = [...card.rules];

          // Remove dragged item
          const [draggedItem] = reorderedRules.splice(draggedRule.index, 1);

          // Insert at new position
          reorderedRules.splice(droppedIndex, 0, draggedItem);

          // Recalculate sequence numbers
          const updatedRules = reorderedRules.map((rule, index) => ({
            ...rule,
            sequence: index + 1, // Ensure correct sequence order
          }));

          return { ...card, rules: updatedRules };
        }
        return card;
      });

      // Assign updated cards back
      updatedCategories[selectedCategory] = updatedCards;

      setDraggedRule(null); // Reset drag state
      return updatedCategories;
    });
  };

  const handleAddRule = () => {
    if (!allocationRuleCategory || !selectedRuleType) return;

    // Validation Rules Mapping
    const validationRules: { [key: string]: { condition: boolean; message: string } } = {
      operator: {
        condition: selectedRuleType === SelectionRuleCategory.JOB_FULFILLMENT_RULE &&
          selectedRuleTrigger === RuleTrigger.DISTANCE_FROM_LOCATION && !selectedOperator,
        message: 'Select Operator',
      },
      distanceInKm: {
        condition: selectedRuleType === SelectionRuleCategory.JOB_FULFILLMENT_RULE &&
          selectedRuleTrigger === RuleTrigger.DISTANCE_FROM_LOCATION && !distanceInKm,
        message: 'Enter distance in KM',
      },
      fulfillmentType: {
        condition: selectedRuleType === SelectionRuleCategory.JOB_FULFILLMENT_RULE &&
          selectedRuleTrigger === RuleTrigger.DISTANCE_FROM_LOCATION && !fulfillmentType,
        message: 'Select Fulfillment type',
      },
      skill: {
        condition: selectedRuleType === SelectionRuleCategory['3PL_DRIVER_SELECTION_RULE'] &&
          selectedRuleTrigger === RuleTrigger.SKILL && !skill,
        message: 'Select Skill',
      },
      tier: {
        condition: selectedRuleType === SelectionRuleCategory['3PL_SELECTION_RULE'] &&
          selectedRuleTrigger === RuleTrigger.TIER && !tier,
        message: 'Select Tier',
      },
    };

    // Check for Validation Errors
    for (const key in validationRules) {
      if (validationRules[key].condition) {
        snackbar({ message: validationRules[key].message, variant: SnackBarVariant.ERROR });
        return;
      }
    }

    // Update Rules
    setRulesByCategory((prevRules) => {
      const updatedRules = { ...prevRules };
      const categoryRules = [...(updatedRules[allocationRuleCategory] || [])];

      const ruleTypeIndex = categoryRules.findIndex(
        (rule) => rule.ruleCategory === selectedRuleType
      );

      if (ruleTypeIndex === -1) return prevRules;

      const ruleObject = { ...categoryRules[ruleTypeIndex] };

      const newRuleBase = {
        ruleName: selectedRuleTrigger!,
        ...(selectedRuleType === SelectionRuleCategory.JOB_FULFILLMENT_RULE &&
          selectedRuleTrigger === RuleTrigger.DISTANCE_FROM_LOCATION &&
          selectedOperator &&
          distanceInKm &&
          fulfillmentType
          ? { operator: selectedOperator, distanceInKm, value: fulfillmentType }
          : {}),
        ...(selectedRuleType === SelectionRuleCategory['3PL_SELECTION_RULE'] &&
          selectedRuleTrigger === RuleTrigger.TIER &&
          tier
          ? { value: tier }
          : {}),
        ...(selectedRuleType === SelectionRuleCategory['3PL_DRIVER_SELECTION_RULE'] &&
          selectedRuleTrigger === RuleTrigger.SKILL &&
          skill
          ? { value: skill }
          : {}),
      };

      const existingRuleIndex = ruleObject.rules.findIndex((rule) => {
        if (
          (selectedRuleType === SelectionRuleCategory['3PL_SELECTION_RULE'] &&
            selectedRuleTrigger === RuleTrigger.TIER) ||
          (selectedRuleType === SelectionRuleCategory['3PL_DRIVER_SELECTION_RULE'] &&
            selectedRuleTrigger === RuleTrigger.SKILL)
        ) {
          return rule.ruleName === newRuleBase.ruleName && rule.value === newRuleBase.value;
        } else {
          return rule.ruleName === newRuleBase.ruleName;
        }
      });

      if (existingRuleIndex !== -1) {
        // Override existing rule but keep its sequence
        const updatedRulesArray = [...ruleObject.rules];
        updatedRulesArray[existingRuleIndex] = {
          ...newRuleBase,
          sequence: updatedRulesArray[existingRuleIndex].sequence, // Keep existing sequence
        };
        ruleObject.rules = updatedRulesArray;
      } else {
        // Add a new rule with a new sequence
        const newRule = {
          ...newRuleBase,
          sequence: ruleObject.rules.length + 1,
        };
        ruleObject.rules = [...ruleObject.rules, newRule];
      }

      categoryRules[ruleTypeIndex] = ruleObject;
      return { ...updatedRules, [allocationRuleCategory]: categoryRules };
    });

    setSelectedRuleType("");
    setSelectedRuleTrigger('');
    resetDependentFields();
  };

  const resetDependentFields = () => {
    setSelectedOperator('');
    setDistanceInKm(0);
    setFulfillmentType('');
    setTier('');
    setSkill('');
  };

  return (
    loading ? (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          width: '100%',
          mt: 2
        }}
      >
        <CircularProgress />
      </Box>
    ) : (
      <Grid container spacing={2}>
        <Grid container item xs={12}>
          <Typography variant="h1">Driver Allocation Rules</Typography>
        </Grid>
        <NuvvenDivider noMargin />
        (
        <Grid item xs={12}>
          <Tabs
            value={allocationRuleTypeTabIndex}
            onChange={(e: React.SyntheticEvent, newValue: number) => {
              const selectedBookingType = activeRules[newValue];

              const categoryMap = {
                [ALLOCATION_RULE_CATEGORY.RENTAL]: ALLOCATION_RULE_CATEGORY.RENTAL,
                [ALLOCATION_RULE_CATEGORY.SUBSCRIPTION]: ALLOCATION_RULE_CATEGORY.SUBSCRIPTION,
                [ALLOCATION_RULE_CATEGORY.COURTESY_CAR]: ALLOCATION_RULE_CATEGORY.COURTESY_CAR,
                [ALLOCATION_RULE_CATEGORY.TEST_DRIVE]: ALLOCATION_RULE_CATEGORY.TEST_DRIVE,
                [ALLOCATION_RULE_CATEGORY.SERVICE]: ALLOCATION_RULE_CATEGORY.SERVICE,
                [ALLOCATION_RULE_CATEGORY.DEFLEET]: ALLOCATION_RULE_CATEGORY.DEFLEET,
                [ALLOCATION_RULE_CATEGORY.BRANCH_TRANSFER]: ALLOCATION_RULE_CATEGORY.BRANCH_TRANSFER
              };

              const category = categoryMap[selectedBookingType.type];
              if (category) {
                setAllocationRuleCategory(category)
              }
              setAllocationRuleTypeTabIndex(newValue)
            }}
            aria-label="icon label tabs example"
            centered
            style={{
              width: "80%",
              margin: "0 auto",
              background: "transparent"
            }}
            TabIndicatorProps={{ style: { display: "none" } }}
          >
            {
              activeRules.length > 0 ? activeRules.map((type, index) => {
                const label =
                type.type === "SUBSCRIPTION"
                  ? type.isContractHire
                    ? "Contract Hire"
                    : "Subscription"
                  : allocationRuleCategoryLabels[type.type];
                return (
                  <Tab
                    style={{
                      marginRight: 10, borderRadius: 6,
                      color: theme.palette.secondary.main,
                      border: allocationRuleTypeTabIndex === index ? `3px solid ${theme.palette.primary.main}` : "",
                      fontWeight: 600,
                      background: "#FFFFFF"
                    }}
                    key={type.type}
                    icon={getBookingTypeIcon(type.type)}
                    label={label}
                  />
                )
              }) : renderMessage("Driver allocation rules are currently disabled for all booking and internal movement journeys in the organization settings. Please enable at least one to proceed.")
            }
          </Tabs>
          {activeRules.length > 0 && (
            <Paper elevation={1} sx={{ padding: 2, margin: "0 auto", width: "100%", my: 2 }}>
              <Grid item xs={12} mt={2}>
                <Grid container spacing={2} wrap="nowrap" my={2}>
                  {/* Rule Type Selection */}
                  <Grid item xs={3}>
                    <FormControl fullWidth variant="outlined">
                      <InputLabel>Rule Type</InputLabel>
                      <Select
                        value={selectedRuleType}
                        onChange={(e) => {
                          setSelectedRuleType(e.target.value as SelectionRuleCategory);
                          setSelectedRuleTrigger("")
                          resetDependentFields()
                        }}
                        label="Rule Type"
                      >
                        {ruleTypeOptions.filter(ruleType => isSupplierPortalEnabled ? ruleType : ruleType.value === SelectionRuleCategory.DRIVER_SELECTION_RULE).map((rule) => (
                          <MenuItem key={rule.value} value={rule.value}>
                            {rule.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  {/* Rule Trigger Selection */}
                  <Grid
                    item
                    xs={3}
                    sx={{
                      display: selectedRuleType ? "block" : "none",
                    }}
                  >
                    <FormControl fullWidth variant="outlined">
                      <InputLabel>Rule Trigger</InputLabel>
                      <Select
                        value={selectedRuleTrigger ?? ''}
                        onChange={(e) => {
                          setSelectedRuleTrigger(e.target.value as RuleTrigger);
                          resetDependentFields();
                        }}
                        label="Rule Trigger"
                      >
                        {selectedRuleType &&
                          ruleTriggerOptions[selectedRuleType]?.map((trigger) => (
                            <MenuItem key={trigger.value} value={trigger.value}>
                              {trigger.label}
                            </MenuItem>
                          ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  {/* Distance-Based Rule Fields */}
                  <Grid
                    item
                    xs={6}
                    sx={{
                      display:
                        selectedRuleType === SelectionRuleCategory.JOB_FULFILLMENT_RULE &&
                          selectedRuleTrigger === RuleTrigger.DISTANCE_FROM_LOCATION
                          ? "flex"
                          : "none",
                      gap: 2,
                    }}
                  >
                    <FormControl fullWidth variant="outlined">
                      <InputLabel>Operator</InputLabel>
                      <Select
                        value={selectedOperator}
                        onChange={(e) => setSelectedOperator(e.target.value as Operator)}
                        label="Operator"
                      >
                        {operatorOptions.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    <TextField
                      fullWidth
                      variant="outlined"
                      label="Distance in KM"
                      type="number"
                      value={distanceInKm || 0}
                      onChange={(e) => setDistanceInKm(Number(e.target.value))}
                    />

                    <FormControl fullWidth variant="outlined">
                      <InputLabel>Fulfillment Type</InputLabel>
                      <Select
                        value={fulfillmentType}
                        onChange={(e) => setFulfillmentType(e.target.value as FulfillmentType)}
                        label="Fulfillment Type"
                      >
                        {fulfillmentTypeOptions.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  {/* Tier Selection Rule */}
                  <Grid
                    item
                    xs={3}
                    sx={{
                      display:
                        selectedRuleType === SelectionRuleCategory["3PL_SELECTION_RULE"] &&
                          selectedRuleTrigger === RuleTrigger.TIER
                          ? "block"
                          : "none",
                    }}
                  >
                    <FormControl fullWidth variant="outlined">
                      <InputLabel>Tier</InputLabel>
                      <Select
                        value={tier}
                        onChange={(e) => setTier(e.target.value)}
                        label="Tier"
                      >
                        {[1, 2, 3].map((option) => (
                          <MenuItem key={option} value={String(option)}>
                            {option}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  {/* Driver Selection Rule */}
                  <Grid
                    item
                    xs={3}
                    sx={{
                      display:
                        selectedRuleType === SelectionRuleCategory['3PL_DRIVER_SELECTION_RULE'] &&
                          selectedRuleTrigger === RuleTrigger.SKILL
                          ? "block"
                          : "none",
                    }}
                  >
                    <FormControl fullWidth variant="outlined">
                      <InputLabel>Skill</InputLabel>
                      <Select
                        value={skill}
                        onChange={(e) => setSkill(e.target.value as PartnerSkills)}
                        label="Skill"
                      >
                        {skillOptions.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
                <Grid container item sx={{ justifyContent: 'flex-end' }}>
                  <Button variant="contained" onClick={handleAddRule} disabled={!(selectedRuleTrigger && selectedRuleType)}>
                    + Add Rule
                  </Button>
                </Grid>
              </Grid>
              <Grid item xs={12} my={2}>
                <NuvvenDivider noMargin />
              </Grid>
              <Grid container spacing={2} display={'flex'} my={2}>
                {allocationRuleCards.map((card, index) => (
                  <Grid item xs={12} md={6} lg={6} key={index}>
                    <Card elevation={3} >
                      <CardContent>
                        <Grid container item mb={3} justifyContent={"space-between"}>
                          <Typography variant="h3">{SelectionRuleCategoryLables[card.ruleCategory]}</Typography>
                          <Tooltip
                            title={
                              <Typography fontSize={15}>
                                {ruleDescriptions[card.ruleCategory]}
                              </Typography>
                            }
                            arrow
                            placement="right"
                          >
                            <InfoIcon />
                          </Tooltip>
                        </Grid>
                        <Grid container spacing={2} sx={{ mt: 2 }}>
                          <Grid item xs={1} />
                          <Grid item xs={1} />
                          <Grid item xs={6}>
                            <Typography variant="body2" fontWeight="bold">
                              Rule
                            </Typography>
                          </Grid>
                          <Grid item xs={2}>
                            <Typography variant="body2" fontWeight="bold">
                              SL
                            </Typography>
                          </Grid>
                          <Grid item xs={1} />
                          <Grid item xs={12}>
                            <NuvvenDivider noMargin />
                          </Grid>
                          {card.rules.length > 0 ? (
                            card.rules.map((rule, idx) => (
                              <Grid
                                container
                                spacing={2}
                                alignItems="center"
                                sx={{ mt: 0.5 }}
                                key={`${card.ruleCategory}-${idx}`}
                                draggable
                                onDragStart={() => handleDragStart(card.ruleCategory, idx)}
                                onDragOver={handleDragOver}
                                onDrop={() => handleDrop(card.ruleCategory, idx)}
                              >
                                <Grid item xs={1} />
                                <Grid item xs={1}>
                                  <DragIndicatorIcon sx={{ transform: "rotate(90deg)", cursor: "grab" }} />
                                </Grid>
                                <Grid item xs={6}>
                                  {card.ruleCategory === SelectionRuleCategory.JOB_FULFILLMENT_RULE ?
                                    `${labels[rule.ruleName]} ${rule.operator === Operator.GREATER_THAN ? '>' : '<'} ${rule.distanceInKm} - ${labels[rule.value!]}`
                                    :
                                    rule.value ? `${labels[rule.ruleName]} - ${labels[rule.value] || rule.value}` : labels[rule.ruleName]
                                  }
                                </Grid>
                                <Grid item xs={2}>
                                  <Typography variant="body2">{rule.sequence}</Typography>
                                </Grid>
                                <Grid item xs={2}>
                                  <IconButton onClick={() => handleDeleteRule(card.ruleCategory, idx)} color="default" size="small">
                                    <DeleteIcon />
                                  </IconButton>
                                </Grid>
                                <Grid item xs={12}>
                                  <NuvvenDivider noMargin />
                                </Grid>
                              </Grid>
                            ))
                          ) : (
                            <Grid item xs={12} display="flex" justifyContent="center" alignItems="center">
                              No rules available
                            </Grid>
                          )}
                        </Grid>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
              <Grid container item sx={{ justifyContent: 'flex-end' }}>
                <Fab variant="extended" size="medium"
                  onClick={handleSave}
                  disabled={saveAllocationRulesLoading}
                >
                  {saveAllocationRulesLoading && (
                    <CircularProgress
                      size={14}
                      style={{ color: "white", marginRight: "10px" }}
                    />
                  )}
                  Save
                </Fab>
              </Grid>
            </Paper>
          )}
        </Grid>
        )
      </Grid>
    )
  )
};

export default DriverAllocationRules;